// client/src/pages/seo/KostenloseDigitaleSpeisekarteDe.jsx
import React, { useEffect, useMemo, useState } from "react";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "check",
    label: "Reicht eine kostenlose digitale Speisekarte für Ihr Restaurant?",
  },
  {
    id: "definition",
    label: "Was ist eine kostenlose digitale Speisekarte?",
  },
  {
    id: "features",
    label: "Was sollte eine kostenlose digitale Speisekarte enthalten?",
  },
  {
    id: "why-free",
    label: "Warum ist ein kostenloser Start für Restaurants sinnvoll?",
  },
  {
    id: "enough",
    label: "Wann reicht ein kostenloser Plan aus?",
  },
  {
    id: "upgrade",
    label: "Wann reicht die kostenlose Version nicht mehr aus?",
  },
  {
    id: "pdf",
    label: "Kostenlose QR-Speisekarte oder PDF hinter einem QR-Code?",
  },
  {
    id: "sales",
    label: "Kann eine kostenlose digitale Speisekarte beim Verkauf helfen?",
  },
  {
    id: "test",
    label: "Wie Sie eine kostenlose digitale Speisekarte in 30 Minuten testen",
  },
  {
    id: "bestplace",
    label: "Wie Best Place Ihnen beim kostenlosen Start hilft",
  },
  {
    id: "faq",
    label: "FAQ zu kostenlosen digitalen Speisekarten",
  },
];

const checklist = [
  "Sie möchten zuerst eine QR-Speisekarte testen.",
  "Ihr Restaurant hat eine kleine oder gut überschaubare Speisekarte.",
  "Ein einfacher Start ohne komplexes Design reicht vorerst aus.",
  "Sie möchten sehen, ob Gäste den QR-Code nutzen.",
  "Sie möchten Namen, Preise oder Verfügbarkeit schneller ändern.",
  "Sie sind noch nicht sicher, ob Sie ein vollständiges Restaurant-Software-System benötigen.",
  "Sie möchten nicht sofort für ein neues Tool bezahlen.",
  "Eine einfache Online-Speisekarte ohne komplexe Automatisierung reicht für den ersten Test aus.",
  "Sie möchten die digitale Speisekarte zuerst an einem Standort testen.",
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function getResult(score) {
  if (score <= 3) {
    return {
      title: "Eine kostenlose Version kann zu einfach sein",
      text:
        "Wenn Sie sofort Reservierungen, erweitertes Design, eine eigene Domain, mehrere Standorte oder tiefere Automatisierung benötigen, reicht ein kostenloser Plan wahrscheinlich nicht lange aus.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "Ein kostenloser Test ist sinnvoll",
      text:
        "Für Ihr Restaurant kann eine kostenlose digitale Speisekarte ein guter erster Schritt sein: QR-Code testen, eine einfache Speisekarte verwalten, die Smartphone-Ansicht prüfen und Reaktionen der Gäste beobachten.",
      tone: "medium",
    };
  }

  return {
    title: "Ein kostenloser Plan ist ein starker Startpunkt",
    text:
      "Ihr Restaurant kann wahrscheinlich mit wenig Risiko testen, ob eine digitale Speisekarte im Alltag hilft: mit Preisen, Fotos, Verfügbarkeit, QR-Zugang und schnelleren Aktualisierungen.",
    tone: "high",
  };
}

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Idee für Illustration">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idee für Illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function KostenloseDigitaleSpeisekarteDe() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "de");
    document.title =
      "Kostenlose digitale Speisekarte für Restaurants: Erst testen, später investieren | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="de"
        langLinks={{
          en: "/en/free-digital-menu-for-restaurants",
          de: "/de/kostenlose-digitale-speisekarte",
          no: "/no/gratis-digital-meny-for-restauranter",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Kostenlose digitale Speisekarte für Restaurants: erst testen,
              später investieren
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Eine kostenlose digitale Speisekarte ist kein billiger Ersatz
                für ein echtes System. Sie ist ein sicherer erster Schritt, um
                zu testen, ob eine QR-Code-Speisekarte im Alltag Ihres
                Restaurants helfen kann.
              </p>

              <p className="seoHero__text">
                Nicht jedes Restaurant braucht sofort ein großes Softwarepaket.
                Aber fast jedes Restaurant kann testen, ob eine digitale
                Speisekarte auf Smartphones gut aussieht, ob Gäste den QR-Code
                verstehen und ob Aktualisierungen einfacher werden.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="de" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Inhalt des Artikels</h2>

        <nav className="qrArticle__nav" aria-label="Artikelnavigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="check" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Reicht eine kostenlose digitale Speisekarte für Ihr Restaurant?</h2>

            <p>
              Wählen Sie die Aussagen aus, die zu Ihrem Restaurant passen. Das
              Ergebnis ist bewusst ehrlich: Nicht jedes Restaurant braucht vom
              ersten Tag an eine komplexe digitale Lösung. Viele Restaurants
              können aber viel früher von einer einfachen QR-Speisekarte
              profitieren, als sie zunächst denken.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="definition" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Was ist eine kostenlose digitale Speisekarte?</h2>

            <p>
              Eine kostenlose digitale Speisekarte ist eine Online-Speisekarte,
              die ein Restaurant ohne Anfangskosten erstellen und testen kann.
              Gäste öffnen sie meistens über einen QR-Code auf dem Tisch oder
              über einen Link auf der Restaurant-Website.
            </p>

            <p>
              Kostenlos sollte nicht unprofessionell bedeuten. Der Zweck einer
              kostenlosen Version ist nicht, sofort jedes Problem zu lösen. Ihr
              Zweck ist, dem Restaurant eine sichere Möglichkeit zu geben, den
              echten Ablauf zu testen.
            </p>

            <h3>Zuerst den echten Ablauf testen</h3>

            <p>
              Ein kostenloser Plan bedeutet: zuerst den echten Ablauf testen und
              danach entscheiden, welche Erweiterung sich lohnt. So kann ein
              Restaurant praktisch prüfen, ob eine digitale Speisekarte in den
              Alltag passt, bevor es sich für ein größeres System entscheidet.
            </p>
          </div>
        </section>

        <section id="features" className="acquaintance">
          <h2>Was sollte eine kostenlose digitale Speisekarte enthalten?</h2>

          <p>
            Eine kostenlose Version muss nicht jede Funktion haben. Sie sollte
            aber genug Nutzen bieten, damit ein Restaurant testen kann, ob die
            Idee im echten Betrieb funktioniert.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Eine Speisekarte erstellen.</li>
            <li className="text__seo-li">Kategorien hinzufügen.</li>
            <li className="text__seo-li">Gerichtenamen hinzufügen.</li>
            <li className="text__seo-li">Preise eintragen.</li>
            <li className="text__seo-li">Beschreibungen hinzufügen.</li>
            <li className="text__seo-li">Fotos testen.</li>
            <li className="text__seo-li">Einen QR-Code nutzen.</li>
            <li className="text__seo-li">Mobile Ansicht prüfen.</li>
            <li className="text__seo-li">Einträge bearbeiten.</li>
            <li className="text__seo-li">Gerichte ausblenden.</li>
            <li className="text__seo-li">Einen einfachen Link teilen.</li>
            <li className="text__seo-li">Einen Teamtest durchführen.</li>
          </ul>

          <p>
            Wenn Sie selbst eine Speisekarte erstellen, einen Preis ändern und
            sie auf einem Smartphone öffnen können, hat die kostenlose Version
            bereits echten praktischen Wert.
          </p>

          <VisualIdea>
            Ein einfacher kostenloser Menütest: eine Kategorie, einige Gerichte,
            Preise, Beschreibungen, ein QR-Code und eine Smartphone-Ansicht.
          </VisualIdea>
        </section>

        <section id="why-free" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Warum ist ein kostenloser Start für Restaurants sinnvoll?</h2>

            <p>
              Restaurants führen neue Werkzeuge selten blind ein. Es geht nicht
              nur um Geld. Es geht auch um Zeit, Gewohnheiten des Teams,
              Verhalten der Gäste und die Frage, ob Menschen das neue Werkzeug
              wirklich nutzen.
            </p>

            <p>
              Deshalb ist ein kleiner Test oft klüger als eine große sofortige
              Veränderung. Ein Restaurant kann mit einigen Produkten starten,
              den QR-Code an einem Tisch testen und sehen, ob der Ablauf klar
              ist.
            </p>

            <h3>Ein kostenloser Test reduziert Risiko</h3>

            <p>
              Eine kostenlose digitale Speisekarte erlaubt dem Restaurant, die
              Idee zu prüfen, bevor eine größere Entscheidung getroffen wird. Der
              Inhaber kann sehen, ob Aktualisierungen einfacher werden, ob der
              QR-Code verstanden wird und ob das Team mit der Speisekarte
              arbeiten kann.
            </p>
          </div>
        </section>

        <section id="enough" className="acquaintance">
          <h2>Wann reicht ein kostenloser Plan aus?</h2>

          <p>
            Ein kostenloser Plan kann ausreichen, wenn Sie zuerst den
            praktischen Nutzen testen möchten und noch keine komplexen Funktionen
            benötigen.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Ihre Speisekarte ist überschaubar.</li>
            <li className="text__seo-li">
              Sie brauchen zuerst nur QR-Zugang.
            </li>
            <li className="text__seo-li">
              Sie möchten Reaktionen der Gäste testen.
            </li>
            <li className="text__seo-li">
              Sie benötigen noch kein stark individuelles Design.
            </li>
            <li className="text__seo-li">Sie haben einen Standort.</li>
            <li className="text__seo-li">
              Sie möchten Preise und Verfügbarkeit schneller aktualisieren.
            </li>
          </ul>

          <h3>Beispiel</h3>

          <p>
            Ein Café mit 25 Artikeln möchte QR-Codes auf einigen Tischen testen:
            Kaffee, Desserts, Frühstück und saisonale Getränke. Für so einen
            Start kann ein kostenloser Plan völlig ausreichen.
          </p>
        </section>

        <section id="upgrade" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Wann reicht die kostenlose Version nicht mehr aus?</h2>

            <p>
              Eine kostenlose Version ist ein Startpunkt. Sie ist nicht immer
              die endgültige Lösung. Sobald die digitale Speisekarte Teil Ihres
              täglichen Betriebs wird, können erweiterte Funktionen sinnvoll
              werden.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Kostenloser Plan reicht oft für</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">Ersten Test.</li>
                  <li className="text__seo-li">Kleine Speisekarte.</li>
                  <li className="text__seo-li">QR-Zugang ausprobieren.</li>
                  <li className="text__seo-li">Einfache Smartphone-Ansicht.</li>
                  <li className="text__seo-li">Erste Fotos und Preise.</li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Upgrade wird sinnvoll für</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">Eigenen Subdomain-Link.</li>
                  <li className="text__seo-li">Mehr Kontrolle über Design.</li>
                  <li className="text__seo-li">Online-Reservierungen.</li>
                  <li className="text__seo-li">Mehrsprachige Speisekarte.</li>
                  <li className="text__seo-li">Bereiche für Aktionen.</li>
                  <li className="text__seo-li">
                    Mehr Kontrolle über Ihre öffentliche Präsenz.
                  </li>
                </ul>
              </div>
            </div>

            <h3>Zuerst prüfen, dann erweitern</h3>

            <p>
              Kostenlos zu starten bedeutet nicht, für immer kostenlos zu
              bleiben. Es bedeutet: zuerst validieren, dann gezielt erweitern.
            </p>
          </div>
        </section>

        <section id="pdf" className="acquaintance">
          <h2>Kostenlose QR-Speisekarte oder PDF hinter einem QR-Code?</h2>

          <p>
            Viele Restaurants starten mit einer PDF hinter einem QR-Code. Das
            ist besser als nichts, aber nicht immer die beste Erfahrung für
            Gäste auf Smartphones.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>PDF hinter QR-Code</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Schnell zu erstellen.</li>
                <li className="text__seo-li">Oft kostenlos.</li>
                <li className="text__seo-li">
                  Weniger komfortabel auf Smartphones.
                </li>
                <li className="text__seo-li">Schwieriger zu aktualisieren.</li>
                <li className="text__seo-li">
                  Schwacher Umgang mit ausverkauften Artikeln.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Digitale Speisekarte</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Mobilfreundlich.</li>
                <li className="text__seo-li">Klare Kategorien.</li>
                <li className="text__seo-li">Einfacher zu aktualisieren.</li>
                <li className="text__seo-li">Fotos und Beschreibungen.</li>
                <li className="text__seo-li">
                  Artikel können ausgeblendet werden.
                </li>
                <li className="text__seo-li">
                  CTA zu Demo oder Reservierung möglich.
                </li>
              </ul>
            </div>
          </div>

          <p>
            Bei einem QR-Code ist der Code selbst nicht der eigentliche Wert.
            Der echte Wert liegt in der Erfahrung nach dem Scannen:
            Lesbarkeit, Geschwindigkeit, Struktur und nützliche Informationen.
          </p>
        </section>

        <section id="sales" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Kann eine kostenlose digitale Speisekarte beim Verkauf helfen?</h2>

            <p>
              Eine digitale Speisekarte garantiert keine höheren Verkäufe. Sie
              kann Angebote aber sichtbarer machen: Vorspeisen, Desserts,
              Getränke, Menüs, Tagesangebote oder saisonale Empfehlungen.
            </p>

            <p>
              Es geht nicht um aggressives Upselling. Es geht um bessere
              Sichtbarkeit. Ein Gast kann nur bestellen, was er bemerkt,
              versteht und im richtigen Moment ansprechend findet.
            </p>

            <h3>Sichtbarkeit ist Teil der Menüerfahrung</h3>

            <p>
              Eine einfache digitale Speisekarte kann bereits helfen, die
              richtigen Artikel klarer zu zeigen: Empfehlungen, saisonale
              Angebote, Getränke, Desserts oder zeitlich begrenzte Produkte.
            </p>
          </div>
        </section>

        <section id="test" className="acquaintance">
          <h2>
            Wie Sie eine kostenlose digitale Speisekarte in 30 Minuten testen
          </h2>

          <p>
            Der erste Test muss nicht perfekt sein. Er muss nur drei Fragen
            beantworten: Können Sie die Speisekarte selbst bearbeiten? Versteht
            der Gast den Ablauf? Lohnt es sich weiterzumachen?
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              5–10 beliebte Artikel auswählen.
            </li>
            <li className="text__seo-li">
              Namen, Preise und kurze Beschreibungen hinzufügen.
            </li>
            <li className="text__seo-li">2–3 Fotos hinzufügen.</li>
            <li className="text__seo-li">Einen QR-Code generieren.</li>
            <li className="text__seo-li">
              Die Speisekarte auf dem Smartphone öffnen.
            </li>
            <li className="text__seo-li">
              Einem Teammitglied zeigen.
            </li>
            <li className="text__seo-li">
              Den QR-Code an einem echten Tisch testen.
            </li>
            <li className="text__seo-li">
              Prüfen, ob die nächste Aktion klar ist.
            </li>
            <li className="text__seo-li">
              Einen Preis ändern oder einen Artikel ausblenden.
            </li>
          </ol>

          <VisualIdea>
            Ein 30-Minuten-Testablauf: einige echte Produkte hinzufügen, den
            QR-Code auf einem Smartphone öffnen, dem Team zeigen und eine kleine
            Menüänderung durchführen.
          </VisualIdea>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="de" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>Häufige Fragen zu kostenlosen digitalen Speisekarten</h2>

          <div className="seoFaq">
            <details>
              <summary>Kann eine digitale Speisekarte wirklich kostenlos sein?</summary>
              <p>
                Ja, eine Basisversion kann kostenlos sein, wenn der Anbieter
                einen kostenlosen Plan für einfache Speisekarten und QR-Code-Zugang
                anbietet.
              </p>
            </details>

            <details>
              <summary>Wo ist der Haken bei einer kostenlosen Version?</summary>
              <p>
                Es sollte keinen versteckten Haken geben. Meist gibt es aber
                Grenzen: weniger Designoptionen, weniger Automatisierung oder
                keine erweiterten Funktionen wie Reservierungen.
              </p>
            </details>

            <details>
              <summary>Reicht eine PDF mit QR-Code aus?</summary>
              <p>
                Für einen sehr einfachen Start kann eine PDF ausreichen. Für die
                tägliche Nutzung auf Smartphones ist eine mobile digitale
                Speisekarte meistens komfortabler.
              </p>
            </details>

            <details>
              <summary>Wann sollte ich auf eine bezahlte Version wechseln?</summary>
              <p>
                Wenn Sie mehr Kontrolle, eine eigene Subdomain, Reservierungen,
                mehr Design, Sprachen oder Marketingfunktionen benötigen.
              </p>
            </details>

            <details>
              <summary>Kann ich ohne Entwickler starten?</summary>
              <p>
                Ja. Die Idee eines solchen Tools ist, dass Inhaber oder
                Administratoren Produkte, Preise, Fotos und Beschreibungen selbst
                verwalten können.
              </p>
            </details>

            <details>
              <summary>Ist das für kleine Restaurants sinnvoll?</summary>
              <p>
                Ja. Kleine Restaurants profitieren oft davon, Menüänderungen
                selbst durchführen zu können, ohne jedes Mal einen neuen Design-
                oder Druckprozess zu starten.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="de"
          group="menu"
          exclude="/de/kostenlose-digitale-speisekarte"
        />
      </article>
    </main>
  );
}